// src/utils/lastGoodSnapshot.ts
// ────────────────────────────────────────────────────────────────────────────
// Shared "last good snapshot" localStorage mirror — SSR-safe instant-paint
// persistence for any React Query feature hook.
//
// Why this exists
// ───────────────
// `useVideoGallery` introduced the IG / YouTube / TikTok instant-paint
// pattern: mirror the last-good server payload to localStorage so the
// FIRST paint after a hard refresh renders cards immediately while a
// fresh authoritative list is fetched in the background. The same
// pattern is requested for operator-digest, sku-catalog, supplier,
// food-cost daily list, etc. — but every hook would otherwise re-
// implement the same six concerns (TTL clamp, byte ceiling, JSON
// round-trip, key composition, quota error swallow, SSR guard).
//
// One typed lib, three pure functions, hook-friendly defaults.
//
// Design properties
// ─────────────────
// • SSR-safe — every function no-ops when `window` is undefined.
// • Never-throws — QuotaExceeded / SecurityError / privacy-mode all
//   silently drop. Telemetry coupling is the caller's choice.
// • Pure-ish — `read` is referentially transparent; `write` is the
//   single side effect.
// • SOLID
//   ▸ SRP — three functions, one for each lifecycle phase.
//   ▸ OCP — `transform` callback lets callers strip server-only fields
//     before write without forking the helper.
//   ▸ ISP — narrow surface; callers don't pay for unused features.
// • Bounded — payload size capped (default 1.5 MB, Safari-quota friendly)
//   and age clamped (default 24 h) so a stale snapshot never haunts a
//   user across days.
//
// Industry reference
// ──────────────────
// IG, YouTube, TikTok, Spotify mirror the last known feed to a
// platform key/value store keyed by user-state-identifying tokens
// (account + filter + view-mode) and gate re-hydration on a bounded
// TTL. Server SWR / `refetchOnMount` immediately overwrites with the
// authoritative payload after first paint.
// ────────────────────────────────────────────────────────────────────────────

export interface SnapshotOptions {
  /**
   * Maximum age of a persisted snapshot, in milliseconds. Older
   * snapshots are treated as stale and not returned. Default 24 h.
   */
  maxAgeMs?: number;
  /**
   * Hard ceiling on the serialized payload size. Writes above this
   * size are silently skipped (no eviction game). Default 1.5 MB.
   */
  maxBytes?: number;
}

export interface PersistedSnapshot<T> {
  data:        T;
  updatedAtMs: number;
}

const DEFAULT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const DEFAULT_MAX_BYTES  = 1_500_000;

/**
 * Read a persisted snapshot if it exists, is parseable, and is within
 * the freshness window.
 *
 * Returns `null` for any of:
 *   • SSR (window undefined)
 *   • Missing key
 *   • Parse failure
 *   • Out-of-window age
 *   • Storage access blocked (privacy mode / disabled cookies)
 *
 * @template T
 * @param key   Fully-qualified localStorage key (caller-namespaced).
 * @param isValid Optional shape validator. Returning `false` discards
 *                the snapshot. Use this for forward-compat against
 *                payload-format changes.
 * @param opts  Freshness window.
 * @returns The persisted snapshot, or `null`.
 */
export function readSnapshot<T>(
  key: string,
  isValid?: (data: unknown) => boolean,
  opts?: SnapshotOptions,
): PersistedSnapshot<T> | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedSnapshot<T>;
    if (!parsed || typeof parsed !== 'object') return null;
    const updatedAt = Number(parsed.updatedAtMs) || 0;
    const age = Date.now() - updatedAt;
    const maxAge = opts?.maxAgeMs ?? DEFAULT_MAX_AGE_MS;
    if (age < 0 || age > maxAge) return null;
    if (isValid && !isValid(parsed.data)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Persist a snapshot to localStorage. Best-effort.
 *
 * Skips the write (no error, no throw) when:
 *   • SSR (window undefined)
 *   • Serialized payload exceeds `maxBytes`
 *   • Storage access throws (quota, privacy mode, etc.)
 *
 * @template T
 * @param key       Fully-qualified localStorage key (caller-namespaced).
 * @param data      The payload to mirror.
 * @param opts      Size ceiling. `updatedAtMs` defaults to `Date.now()`.
 * @param updatedAtMs Optional explicit clock reading for testability.
 */
export function writeSnapshot<T>(
  key: string,
  data: T,
  opts?: SnapshotOptions & { updatedAtMs?: number },
): void {
  try {
    if (typeof window === 'undefined') return;
    const updatedAtMs = opts?.updatedAtMs ?? Date.now();
    const payload = JSON.stringify({ data, updatedAtMs } satisfies PersistedSnapshot<T>);
    const maxBytes = opts?.maxBytes ?? DEFAULT_MAX_BYTES;
    if (payload.length > maxBytes) return;
    window.localStorage.setItem(key, payload);
  } catch {
    // QuotaExceeded / SecurityError / privacy-mode — silently no-op.
  }
}

/**
 * Remove a persisted snapshot. Useful on explicit "purge / log out".
 * Never throws.
 */
export function clearSnapshot(key: string): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(key);
  } catch {
    // Ignore.
  }
}

/**
 * Compose a stable, namespaced localStorage key from parts.
 *
 *   buildSnapshotKey('pac:gallery:v1', tenant, includeInitiated)
 *     → 'pac:gallery:v1:tenant42:gallery'
 *
 * Convention: prefix encodes the schema version so a payload-shape
 * change rotates keys cleanly (no in-place migration needed).
 */
export function buildSnapshotKey(prefix: string, ...parts: Array<string | number | boolean | null | undefined>): string {
  const segs = parts.map((p) => {
    if (p === null || p === undefined) return 'null';
    if (typeof p === 'boolean') return p ? 'true' : 'false';
    return String(p);
  });
  return [prefix, ...segs].join(':');
}
